import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { QueryClient } from '@tanstack/react-query'
import type {
    EInvoice,
    EInvoiceResolutionSource,
    ManualIssueInput,
    SkipInput,
} from '@/lib/api/einvoice-api'
import { einvoiceApi } from '@/lib/api/einvoice-api'
import { invalidateRejectedEInvoiceCredential } from '@/hooks/use-einvoice-credential'
import { einvoiceKeys } from '@/hooks/use-einvoice-query'
import { invalidateEInvoiceDocument } from '@/hooks/use-einvoice-observation'
import { orderKeys } from '@/hooks/use-sales-order-query'
import { ApiError } from '@/lib/api/client'
import { m } from '@/lib/paraglide/messages'

/** The order's invoice row, the order lists (status badge, pending count) and
 *  any cached document all change when a person closes or reopens an order. */
/** Another person already decided, or the invoice moved on: the card must show
 *  the current state rather than ask the user to reload. */
const STALE_CODES = [
    'EINVOICE_STATUS_CHANGED',
    'EINVOICE_NOT_RESOLVABLE',
    'EINVOICE_ALREADY_ISSUED_ON_MISA',
]

function refreshOnStaleCard(
    queryClient: QueryClient,
    orderId: string,
    error: unknown,
) {
    invalidateRejectedEInvoiceCredential(queryClient, error)
    if (error instanceof ApiError && STALE_CODES.includes(error.code)) {
        invalidateEInvoiceDocument(queryClient, orderId)
        void queryClient.invalidateQueries({
            queryKey: einvoiceKeys.detail(orderId),
        })
        void queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
    }
}

function applyResolution(
    queryClient: QueryClient,
    orderId: string,
    data: EInvoice | undefined,
) {
    invalidateEInvoiceDocument(queryClient, orderId)
    // No data = the order has no invoice row any more: that is a known answer.
    queryClient.setQueryData(einvoiceKeys.detail(orderId), data ?? null)
    void queryClient.invalidateQueries({
        queryKey: einvoiceKeys.detail(orderId),
    })
    void queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
    void queryClient.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
}

export function useManualIssueEInvoice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({
            orderId,
            input,
        }: {
            orderId: string
            input: ManualIssueInput
        }) => (await einvoiceApi.manualIssue(orderId, input)).data,
        onSuccess: (data, { orderId }) => {
            applyResolution(queryClient, orderId, data)
            toast.success(m.einvoice_manual_saved())
        },
        onError: (error, { orderId }) =>
            refreshOnStaleCard(queryClient, orderId, error),
    })
}

export function useSkipEInvoice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({
            orderId,
            input,
        }: {
            orderId: string
            input: SkipInput
        }) => (await einvoiceApi.skip(orderId, input)).data,
        onSuccess: (data, { orderId }) => {
            applyResolution(queryClient, orderId, data)
            toast.success(m.einvoice_skipped_saved())
        },
        onError: (error, { orderId }) =>
            refreshOnStaleCard(queryClient, orderId, error),
    })
}

export function useReopenEInvoice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({
            orderId,
            source,
        }: {
            orderId: string
            source: EInvoiceResolutionSource
        }) => (await einvoiceApi.reopen(orderId, source)).data,
        onSuccess: (data, { orderId }) => {
            applyResolution(queryClient, orderId, data)
            toast.success(m.einvoice_reopened())
        },
        onError: (error, { orderId }) =>
            refreshOnStaleCard(queryClient, orderId, error),
    })
}
